import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useViewMode } from '../hooks/useViewMode';
import {
  Search, ListChecks, Pencil, Repeat, PlusSquare, Replace, GitCompareArrows, CalendarCheck, Package,
  UserX, Tv, CalendarClock, Ticket, History, ScrollText, KeyRound, ChevronRight, CheckCircle, AlertTriangle, ArrowLeft,
  Tv2, RefreshCw,
} from 'lucide-react';
import { gtplSaathiAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import useDarkReady from '../theme/useDarkReady';
import { Screen, PageHead, Notice } from '../components/gtpl/GtplKit';

// The GTPL start page: every GTPL screen grouped by what the operator is trying to do, with a
// plain-words line on what it does. `name` is the menu name the screen has always had (kept so
// staff trained on the old menu can still find it; it is also searchable). `right` is the
// gtplSaathi module action the screen needs, so a tile is hidden when the user couldn't use it.
// `tone` is the group's colour (see styles/gtpl.css): blue everyday, purple boxes, amber = spends wallet,
// green = checks, red = trouble.
const GROUPS = [
  {
    label: 'Everyday work', tone: 'info', items: [
      { to: '/gtpl-saathi-subscriber', icon: Search, title: 'Find a customer on GTPL', name: 'Subscriber Lookup', right: 'search',
        desc: 'Look up by box number, account or mobile. Then renew, suspend / activate, or place a new order.' },
      { to: '/gtpl-saathi-multi-renew', icon: ListChecks, title: 'Renew several customers', name: 'Multi Renew', right: 'renew',
        desc: 'Paste a list of box numbers or accounts and renew them in one go.' },
      { to: '/gtpl-saathi-modify', icon: Pencil, title: 'Edit a customer’s details on GTPL', name: 'Modify', right: 'modify',
        desc: 'Change name, mobile or address on GTPL.' },
      { to: '/gtpl-saathi-ticket', icon: Ticket, title: 'Support tickets', name: 'eSupport Tickets', right: 'viewTickets',
        desc: 'Raise a ticket with GTPL and see the ones already raised.' },
    ],
  },
  {
    label: 'Boxes', tone: 'adv', items: [
      { to: '/gtpl-saathi-hardware-replacement', icon: Repeat, title: 'Replace a customer’s box', name: 'Hardware Replacement', right: 'hardwareReplacement',
        desc: 'Swap the old set-top box for another one on GTPL.' },
      { to: '/gtpl-box-stock', icon: Package, title: 'Spare boxes we have', name: 'Box Stock', right: 'boxStock',
        desc: 'How many spare boxes GTPL shows for each entity, and which boxes sit in the wrong entity.' },
      { to: '/gtpl-stb-mismatch', icon: Tv, title: 'Box differs from CRM', name: 'GTPL STB Mismatch', right: 'stbCheck',
        desc: 'Customers whose box model or number on GTPL is not the same as in the CRM.' },
    ],
  },
  {
    label: 'Bulk jobs (spend GTPL wallet)', tone: 'warn', items: [
      { to: '/gtpl-alacarte-bulk', icon: PlusSquare, title: 'Add one channel to everyone', name: 'A la Carte Bulk', right: 'view',
        desc: 'Add a single channel (for example free AASTHA) to every active customer, in rounds.' },
      { to: '/gtpl-bulk-change-order', icon: Replace, title: 'Change package for many', name: 'Bulk Change Order', right: 'placeOrder',
        desc: 'Same-package Change Order for all eligible customers, so the GTPL date matches the CRM.' },
    ],
  },
  {
    label: 'Check GTPL against the CRM', tone: 'ok', items: [
      { to: '/gtpl-vs-crm', icon: GitCompareArrows, title: 'Compare customer data', name: 'GTPL vs CRM', right: 'crmCheck',
        desc: 'Mobile, entity and box of every active customer compared with GTPL. Can fix the mobile on GTPL.' },
      { to: '/gtpl-expiry-check', icon: CalendarCheck, title: 'Compare expiry dates', name: 'Expiry Check', right: 'expiryCheck',
        desc: 'Which customers have a different expiry date on GTPL and in the CRM. Only checks, changes nothing.' },
      { to: '/gtpl-not-in-crm', icon: UserX, title: 'On GTPL but not in CRM', name: 'GTPL Not In CRM', right: 'orphanCheck',
        desc: 'Active GTPL accounts that have no customer in the CRM.' },
      { to: '/gtpl-90-days', icon: CalendarClock, title: 'Inactive accounts (90 days)', name: '90 Days (Account)', right: 'inactiveAccounts',
        desc: 'Every inactive GTPL account with its box. Mark the box issue as pending or done.' },
    ],
  },
  {
    label: 'When something goes wrong', tone: 'bad', items: [
      { to: '/gtpl-saathi-retry-queue', icon: History, title: 'Waiting and failed actions', name: 'Retry Queue', right: 'view',
        desc: 'Actions that could not reach GTPL are kept here and tried again. Retry or cancel them.' },
      { to: '/gtpl-saathi-logs', icon: ScrollText, title: 'What was done on GTPL', name: 'Logs', right: 'view',
        desc: 'Every order or change sent to GTPL, who did it, and whether it worked.' },
      { to: '/gtpl-saathi-settings', icon: KeyRound, title: 'GTPL login and settings', name: 'Session & Settings', right: 'view',
        desc: 'Is the GTPL login working? Paste a fresh login, set automatic login and automatic jobs.' },
    ],
  },
];

// Wraps every GTPL route. In app view it adds one sticky "GTPL Home" bar above each GTPL
// screen so there is always a single, same-place way back to the menu above. Web view: no change.
export function GtplShell() {
  const { viewMode } = useViewMode();
  const { pathname } = useLocation();
  const isApp = viewMode === 'app' || window.innerWidth < 640;
  return (
    <>
      {isApp && pathname !== '/gtpl' && (
        <Link to="/gtpl" className="gx-back">
          <ArrowLeft size={18} /> GTPL Home
        </Link>
      )}
      <div className={isApp ? 'gtpl-app' : undefined}><Outlet /></div>
    </>
  );
}

export default function GtplHomePage() {
  useDarkReady();
  const { hasModuleRight } = useAuth();
  const [q, setQ] = useState('');
  const [session, setSession] = useState(null);
  const [counts, setCounts] = useState(null);

  const load = () => {
    gtplSaathiAPI.getSessionStatus().then(r => setSession(r.data.data)).catch(() => {});
    gtplSaathiAPI.getPendingTasks({ page: 1, limit: 1 }).then(r => setCounts(r.data.statusCounts || {})).catch(() => {});
  };
  useEffect(load, []);

  const needle = q.trim().toLowerCase();
  const groups = GROUPS
    .map(g => ({
      ...g,
      items: g.items.filter(i => hasModuleRight('gtplSaathi', i.right)
        && (!needle || `${i.title} ${i.name} ${i.desc} ${g.label}`.toLowerCase().includes(needle))),
    }))
    .filter(g => g.items.length);

  const loginOk = session && session.configured && session.lastTestResult !== 'failed';
  const queued = counts?.queued || 0;
  const failed = counts?.failed || 0;

  return (
    <Screen>
      <PageHead icon={Tv2} tone="info" title="GTPL" sub="Everything you do on GTPL, grouped by the job.">
        <button type="button" className="btn btn-outline btn-sm" onClick={load}><RefreshCw size={14} /> Refresh</button>
      </PageHead>

      {/* Health strip: the two things that explain most "GTPL is not working" calls. */}
      {session && (loginOk
        ? <Notice tone="ok" to="/gtpl-saathi-settings" icon={CheckCircle}><b>GTPL login is working</b></Notice>
        : <Notice tone="bad" to="/gtpl-saathi-settings" icon={AlertTriangle} title="GTPL login needs attention.">Tap to log in again. Actions wait in the queue until then.</Notice>)}
      {(queued > 0 || failed > 0) && (
        <Notice tone={failed > 0 ? 'bad' : 'warn'} to="/gtpl-saathi-retry-queue" icon={AlertTriangle}
          title={`${queued > 0 ? `${queued} waiting to retry` : ''}${queued > 0 && failed > 0 ? ' · ' : ''}${failed > 0 ? `${failed} failed` : ''}`}>
          Tap to see them.
        </Notice>
      )}

      <input className="form-control" type="search" value={q} onChange={e => setQ(e.target.value)}
        placeholder="Search GTPL screens (renew, box, expiry, login…)" style={{ marginBottom: 16, minHeight: 46 }} />

      {groups.map(g => (
        <section key={g.label} className={`gx-group is-${g.tone}`}>
          <h2 className="gx-group-label"><i className="gx-dot" />{g.label}</h2>
          <div className="gx-tiles">
            {g.items.map(({ to, icon: Icon, title, name, desc }) => (
              <Link key={to} to={to} className="gx-tile">
                <span className="gx-tile-ico"><Icon size={20} /></span>
                <span className="gx-tile-text">
                  <span className="gx-tile-title">{title}</span>
                  <span className="gx-tile-desc">{desc}</span>
                  <span className="gx-tile-name">Menu name: {name}</span>
                </span>
                <ChevronRight size={18} className="gx-tile-go" />
              </Link>
            ))}
          </div>
        </section>
      ))}
      {groups.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: '.88rem', padding: 12 }}>Nothing matches.</div>}
    </Screen>
  );
}
